import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Avatar, Icon, Paper } from '@mui/material';

import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';
import { MijnOrganisatieListItem } from '../../../models/user/mijnOrganisatieListItem';
import { jwtDecode } from 'jwt-decode';
import { userManagementService } from '../../../services/user-management.service';
import { authService } from '../../../services/auth.service';
import { getRoleLabel } from '../../../models/user/userRole';

interface TokenClaims {
  roles?: string[]; // Adjust this based on your token structure
}

const MijnGegevens: React.FC = () => {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();
  const account = accounts[0];

  const [organisatie, setMijnOrganisatie] = useState<MijnOrganisatieListItem | null>(null);
  const [roles, setRoles] = useState<string[] | undefined>([]);
  const [initials, setInitials] = useState<string | null>(null);
  const [decodedToken, setDecodedToken] = useState<TokenClaims | null>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const hasMijnGegevensData = useRef(false);


  
  useEffect(() => {
    // Ensure the fetch happens only once
    if (!hasMijnGegevensData.current) {
      fetchMijnGegevens();
      hasMijnGegevensData.current = true; // Mark as fetched
    }
  }, []); // Empty dependency array ensures this runs once when the component mounts

  const fetchMijnGegevens = async () => {
    setLoading(true);
    try {
      // Mijn gegevens
      //console.log('accounts', accounts);
      //console.log('instance', instance);

      const idToken = account?.idToken; // account?.idTokenClaims?.idToken;

      // Decode the token to read claims
      let roles: string[] | undefined;
      if (idToken) {
        const decodedToken = jwtDecode<TokenClaims>(idToken as string); // Cast idToken to string
        //console.log('decodedToken', decodedToken);
        setDecodedToken(decodedToken);
        roles = decodedToken.roles;
        setRoles(roles);
      }

      const fetchedInitials = await authService.getInitials();
      setInitials(fetchedInitials);

      const storedOrganisatie = userManagementService.getStoredOrganisatie();
      if (storedOrganisatie) {
        setMijnOrganisatie(storedOrganisatie);
      }
      
      setLoading(false);
    }
    catch (error: any) {
      // If token refresh failed, redirect to the logout page
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError(error.message || 'Er is een fout opgetreden');
      setLoading(false);
    }
  };

  return (
    <div>
      {isAuthenticated && (
        <>
          <Paper sx={{ width: '100%', padding: '20px', margin: '0px auto 50px' }}>
            <div className="container business-card">
              <div className="row">
                <div className="col-lg-4 col-md-4 col-sm-12">
                  <Avatar sx={{ marginTop: '30px', width: 160, height: 160, padding: 0, fontSize: 100 }} className="purple">
                    {initials || <Icon style={{ fontSize: "140px" }}>person</Icon> } 
                    {/* <FontAwesomeIcon icon={faUser} /> */}
                  </Avatar>
                </div>
                <div className="col-lg-8 col-md-8 col-sm-12">
                  
                  {account && (
                    <>
                      <div className="name">
                        <h5>{account?.name}</h5>
                        {organisatie && organisatie.id && 
                          <>
                            {getRoleLabel(organisatie.userType)}
                            {roles && roles?.includes('admin') && (
                              <> ({roles.map((role, index) => (<span key={index}>{role}</span> ))})</>
                            )}
                          </>
                        }
                      </div>
                      <div className="contact">
                        <label>E-mail:</label> {account?.username.toLowerCase()}
                      </div>
                    </>
                  )}
                  {organisatie && organisatie.id && 
                    <>
                     <label>Organisatie:</label> {organisatie.displayName}
                    </>
                  }
                </div>
              </div>
            </div>

            {/* {account && (
              <>
                <label>Naam:</label> {account?.name}<br/>
                <label>Account:</label> {account?.username.toLowerCase()}<br/>
              </>
            )}
            {organisatie && organisatie.id && 
              <>
                <label>Huidige organisatie:</label> {organisatie.displayName}<br/>
                <label>Rol in deze organisatie:</label> {getRoleLabel(organisatie.userType)}
              </>
            } */}
            
          </Paper>
          
          {decodedToken && roles?.includes('admin') && // Only show decoded token for Admins
            <>
              <Paper sx={{ width: '100%', padding: '20px', margin: '0px auto' }}>
                <h2>Decoded Token</h2>
                <pre className="scrollable-area scroll-x">
                  <code>{JSON.stringify(decodedToken, null, 2)}</code> {/* Pretty-print JSON */}
                </pre>
              </Paper>
            </>
          }
        </>
      )}

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}

    </div>
  );
};

export default MijnGegevens;
